import { LinearGradient } from 'expo-linear-gradient'
import type { ReactNode } from 'react'
import { Platform, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'

import { Button, type TDefaultButtonProps } from '@/src/elements/button/default'
import { GradientTypography } from '@/src/elements/text/gradient'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { useTranslate } from '@/src/lang/useTranslate'

import { useWizardStore } from '../../stores/useWizardStore'

type TWizardContentLayoutProps = {
  title: string
  children: ReactNode
  testID?: string
  previousButtonProps?: TDefaultButtonProps
  nextButtonProps?: TDefaultButtonProps
}

export const WizardContentLayout = ({
  title,
  children,
  testID,
  previousButtonProps,
  nextButtonProps,
}: TWizardContentLayoutProps): ReactNode => {
  const { t } = useTranslate()
  const { lessThan } = useDimensions()
  const { currentStepName, nextStep, prevStep } = useWizardStore()

  return (
    <View className="web:h-dvh web:max-h-dvh web:gap-10 native:flex-1">
      <ScrollView
        testID={testID}
        className="web:h-full native:flex-1"
        showsVerticalScrollIndicator={!lessThan.md}
        contentContainerClassName="gap-10"
      >
        {Platform.OS !== 'web' && (
          <View className="flex h-40 w-full items-center">
            <GradientTypography
              colors="primary"
              black
              center
              size="2xl"
              classNameWrapper="h-full native:w-full px-6"
              className="flex min-h-full flex-1 px-6 text-center font-black"
            >
              {title}
            </GradientTypography>
          </View>
        )}
        {Platform.OS === 'web' && (
          <GradientTypography colors="primary" black center size="2xl" className="px-6">
            {title}
          </GradientTypography>
        )}
        <View className="grow px-4 web:pt-18 pb-18">{children}</View>
      </ScrollView>
      <View className="w-full web:absolute web:bottom-16">
        <LinearGradient colors={['rgba(255, 255, 255, 0.00) 0%', '#FFFFFF']} className="absolute -top-18 h-18 w-full" />

        <View className="flex-row justify-between bg-white px-6 pb-4">
          <Button
            onPress={prevStep}
            colors="secondary"
            variant="faded"
            {...(currentStepName !== 'theme' && { startIcon: ICON.ARROW_LEFT })}
            {...previousButtonProps}
          >
            {previousButtonProps?.children || t('word.previous')}
          </Button>
          <Button
            onPress={nextStep}
            testID={TEST_ID.ME.WIZARD.BTN_NEXT}
            colors="secondary"
            variant="solid"
            endIcon={ICON.ARROW_RIGHT}
            {...nextButtonProps}
          >
            {nextButtonProps?.children || t('word.next')}
          </Button>
        </View>
      </View>
    </View>
  )
}
